import {
  ChangeDetectionStrategy,
  Component, EventEmitter,
  Input, Output
} from '@angular/core';
import { Game, Playoff, Sport, Team } from '@mtgame/core';
import { getHeaderHeight } from '@utils/layout';

@Component({
  selector: 'mtg-tournament-playoff-bracket',
  templateUrl: './tournament-playoff-bracket.component.html',
  styleUrls: ['./tournament-playoff-bracket.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TournamentPlayoffBracketComponent {
  @Input()
  games: Game[];
  @Input()
  playoff: Playoff;
  @Input()
  gameRoute = '';
  @Input()
  canManage: boolean;
  @Input()
  openGameInNewWindow: boolean;
  @Output()
  gameForm = new EventEmitter<{playoffNumber: string, gameId: number}>();
  @Input()
  currentRound: number;
  @Input()
  loserBracket: boolean;
  @Input()
  winnerBracket: boolean;
  @Input()
  sport: Sport;
  headerHeight = getHeaderHeight();

  get isDoubleElimination(): boolean {
    return this.loserBracket || this.winnerBracket;
  }

  get toursCount(): number {
    if (this.winnerBracket) {
      return (Math.log(this.playoff.settings.teamsCount) / Math.log(2)) + 1;
    }
    if (this.loserBracket) {
      const singleEliminationSemifinalRound = this.semiFinalRoundNumber;
      return (singleEliminationSemifinalRound * 2) - 2;
    }
    return Math.log(this.playoff.settings.teamsCount) / Math.log(2);
  }

  get semiFinalRoundNumber(): number {
    const toursCount = (Math.log(this.playoff.settings.teamsCount) / Math.log(2)) + 1;
    return toursCount - 2;
  }

  getRoundNumber(i: number, g: number = 0): any {
    const tour = i + 1;
    const winnerPlace = this.playoff.winnerPlace;
    let finalTitle = 'Финал';
    let thirdPlaceTitle = '3 место';
    if (winnerPlace > 1) {
      finalTitle = winnerPlace + ' место';
      thirdPlaceTitle = (winnerPlace + 2) + ' место';
    }
    if (tour === this.toursCount) {
      if (g === 0) {
        if ([106, 107].includes(this.playoff.id)) {
          return '5 место';
        }
        return finalTitle;
      } else {
        if ([106, 107].includes(this.playoff.id)) {
          return '7 место';
        }
        return thirdPlaceTitle;
      }
    } else if (tour === this.toursCount - 1) {
      return 'Полуфинал';
    } else {
      return 'Раунд ' + tour;
    }
  }

  getMatchesCount(i: number): number {
    if (this.winnerBracket) {
      const tour = i + 1;
      if (tour === this.toursCount - 1) {
        return 2;
      } else {
        return this.playoff.settings.teamsCount / Math.pow(2, tour);
      }
    }

    if (this.loserBracket) {
      let toursCount = this.toursCount;
      let power = this.semiFinalRoundNumber;
      const tour = i + 1;
      while (toursCount > tour) {
        if (toursCount % 2 !== 0) {
          power -= 1;
        }
        toursCount -= 1;
      }
      return this.playoff.settings.teamsCount / Math.pow(2, power);
    }

    return this.playoff.settings.teamsCount / Math.pow(2, i + 1);
  }

  generateArray(length: number): any[] {
    return new Array(length);
  }

  getGames(tourNumber: number, gameNumber: number): Game[] {
    if (this.loserBracket) {
      let newGameNumber;
      if (tourNumber % 2 === 0) {
        newGameNumber = (gameNumber * 2) + 1;
      } else {
        newGameNumber = gameNumber + 1;
      }
      return this.games.filter(game => game.playoffNumber === `l_${tourNumber + 1}_${newGameNumber}`).sort((a, b) => a.id - b.id);
    }
    return this.games.filter(game => game.playoffNumber === `${tourNumber + 1}_${gameNumber + 1}`).sort((a, b) => a.id - b.id);
  }

  getGameNumber(tour: number, game: number): number {
    if (this.loserBracket) {
      let gamesCount = 0;
      while (tour) {
        gamesCount += this.getMatchesCount(tour - 1);
        tour--;
      }
      return 1 + game + gamesCount;
    }
    if (tour + 1 === this.toursCount && this.winnerBracket) {
      return 2 + game + this.playoff.settings.teamsCount - this.playoff.settings.teamsCount / Math.pow(2, tour);
    }
    return 1 + game + this.playoff.settings.teamsCount - this.playoff.settings.teamsCount / Math.pow(2, tour);
  }

  getNextGameTeam(tour: number, game: number, team: number = 0): any {
    if (this.loserBracket) {
      let number;
      if (tour === 0) {
        if (team % 2 === 0) {
          number = ((game + 1) * 2) - 1;
        } else {
          number = ((game + 1) * 2);
        }
        return !this.sport.isHockey() ? 'Проигравший ' + number : '';
      }
      const matchesPrevRound = this.getMatchesCount(tour - 1);
      const gameNumber = this.getGameNumber(tour, game);
      if (tour % 2 === 0) {
        number = (gameNumber - matchesPrevRound) + game + team;
        return !this.sport.isHockey() ? 'Победитель L' + number : '';
      } else {
        if (team === 1) {
          const winnerTour = (tour + 1) / 2;
          const matchesInRound = this.getMatchesCount(tour);
          let winnerGameNumber;
          if (matchesInRound >= 8) {
            winnerGameNumber = matchesInRound - game - 1;
          } else {
            if (game % 2 === 0) {
              winnerGameNumber = game + 1;
            } else {
              winnerGameNumber = game - 1;
            }
          }
          number = this.getWinnerGameNumber(winnerTour, winnerGameNumber);
          return !this.sport.isHockey() ? 'Проигравший ' + number : '';
        } else {
          number = gameNumber - matchesPrevRound;
          return !this.sport.isHockey() ? 'Победитель L' + number : '';
        }
      }
    } else if (this.winnerBracket) {
      if (tour === 0) {
        return;
      }
      const matchesPrevRound = this.getMatchesCount(tour - 1);
      const gameNumber = this.getGameNumber(tour, game);
      let teamNumber = (gameNumber - matchesPrevRound) + game + team;
      if (tour + 1 === this.toursCount) {
        if (game === 1) {
          teamNumber = (gameNumber - 1 - matchesPrevRound) + team;
          return !this.sport.isHockey() ? 'Проигравший ' + teamNumber : '';
        }
      }
      if (tour + 2 === this.toursCount) {
        if (team === 1) {
          teamNumber = gameNumber - 4;
          return !this.sport.isHockey() ? 'Победитель L' + teamNumber : '';
        } else {
          teamNumber = gameNumber - matchesPrevRound;
          return !this.sport.isHockey() ? 'Победитель ' + teamNumber : '';
        }
      }
      return !this.sport.isHockey() ? 'Победитель ' + teamNumber : '';
    } else {
      if (tour === 0) {
        return;
      }
      const matchesPrevRound = this.getMatchesCount(tour - 1);
      const gameNumber = this.getGameNumber(tour, game);
      let t = (gameNumber - matchesPrevRound) + game + team;
      if (tour + 1 === this.toursCount) {
        if (game === 1) {
          t = (gameNumber - 1 - matchesPrevRound) + team;
          return !this.sport.isHockey() ? 'Проигравший ' + t : '';
        }
      }
      return !this.sport.isHockey() ? 'Победитель ' + t : '';
    }
  }

  getWinnerGameNumber(tour: number, game: number): number {
    return 1 + game + this.playoff.settings.teamsCount - this.playoff.settings.teamsCount / Math.pow(2, tour);
  }

  getTitle(tour: number, game?: number): string {
    if (this.loserBracket) {
      return `Проигравшие. Раунд ${tour + 1}`;
    }
    const winnerPlace = this.playoff.winnerPlace;
    let finalTitle = 'Финал';
    let thirdPlaceTitle = '3 место';
    if (winnerPlace > 1) {
      finalTitle = winnerPlace + ' место';
      thirdPlaceTitle = (winnerPlace + 2) + ' место';
    }
    if (this.winnerBracket) {
      if (tour === this.toursCount - 1) {
        if (game === 0) {
          return finalTitle;
        } else {
          return thirdPlaceTitle;
        }
      } else if (tour === this.toursCount - 2) {
        return 'Полуфинал';
      } else {
        return `Раунд ${tour + 1}`;
      }
    }
    const matchesCount = this.getMatchesCount(tour);
    if (matchesCount === 4) {
      return 'Четвертьфинал';
    } else if (matchesCount === 2) {
      if ([106, 107].includes(this.playoff.id)) {
        return '';
      }
      return 'Полуфинал';
    } else if (matchesCount === 1) {
      return finalTitle;
    } else {
      return `1/${matchesCount} финала`;
    }
  }

  getWinnerTeam(games: Game[]): Team {
    if (games.length === 0) {
      return;
    }
    const openGames = games.filter(g => !g.isClosed);
    if (openGames.length > 0) {
      return;
    }
    const winners = games.reduce((acc, item) => ({
      ...acc,
      [item.teamWinnerId]: (acc[item.teamWinnerId] || 0) + 1
    }), {});
    const winnerTeamId = +Object.keys(winners).sort((a, b) => winners[a] > winners[b] ? -1 : 1)[0];
    return games[0].team.id === winnerTeamId
      ? games[0].team
      : games[0].competitorTeam;
  }
}
